import { SHOPIFY_STORE_DOMAIN, SHOPIFY_STOREFRONT_API_TOKEN } from '@portal/config/base';
import { safeLocalStorage } from '@portal/shared/utils/safeLocalStorage';
import { StoreCode } from 'schema/storeCode';
import { cartByIdQuery } from '../queries/cartById';
import { CartDetails } from '../types';
import { formatCartLines, shopifyFetch } from '../utils';
import { createShopifyCart } from './cartCreate';

async function cartById({
  domain,
  token,
  cartId
}: {
  cartId: string;
  domain: string;
  token: string;
}): Promise<CartDetails> {
  try {
    const res = await shopifyFetch<any>({
      query: cartByIdQuery,
      shopifyDomain: domain,
      accessToken: token,
      variables: {
        cartId
      }
    });

    if (!res.body.data || !res.body.data.cart) {
      throw new Error('Cart not found');
    }

    const rawCartData = res.body.data.cart;
    return {
      ...rawCartData,
      lines: formatCartLines(rawCartData)
    };
  } catch (error) {
    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(`Error fetching cart by ID: ${error.message}`);
      throw new Error(`Failed to fetch cart: ${error.message}`);
    } else {
      // eslint-disable-next-line no-console
      console.error('Unknown error occurred while fetching cart');
      throw new Error('An unknown error occurred while fetching the cart');
    }
  }
}

export const getShopifyCartById = async (
  storeCode: StoreCode,
  cartId: string
): Promise<CartDetails> => {
  try {
    const cart = await cartById({
      domain: SHOPIFY_STORE_DOMAIN[storeCode],
      token: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
      cartId
    });

    return cart;
  } catch (error) {
    // If cart not found, create a new empty cart
    if (error instanceof Error && error.message.includes('Cart not found')) {
      // eslint-disable-next-line no-console
      console.warn(`Cart ${cartId} not found. Creating new empty cart...`);

      // Remove the invalid cart ID from localStorage
      const storedCartId = safeLocalStorage.getItem('cartId');
      if (storedCartId === cartId) {
        safeLocalStorage.removeItem('cartId');
      }

      // Create a new empty cart
      const newCart = await createShopifyCart(storeCode, {
        lines: [],
        attributes: []
      });

      // Store the new cart ID
      safeLocalStorage.setItem('cartId', newCart.id);

      // Return the full cart details
      return await cartById({
        domain: SHOPIFY_STORE_DOMAIN[storeCode],
        token: SHOPIFY_STOREFRONT_API_TOKEN[storeCode],
        cartId: newCart.id
      });
    }

    // Re-throw other errors
    throw error;
  }
};
